/**
 * DynamicView - Gemini-style adaptive interface
 * 
 * The AI can generate custom UI components based on the query type:
 * - Travel queries → Flight/hotel cards with booking buttons
 * - Data queries → Charts and tables
 * - Comparison queries → Side-by-side comparison views
 * - List queries → Scrollable carousels
 * - Action queries → Step-by-step wizards
 * 
 * This is the "north star" — SuperJarvis generates the UI it needs.
 */
import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  Mail,
  Zap,
  TrendingUp,
  ChevronRight,
  CheckSquare,
  Globe,
  ArrowUp,
  AlertCircle
} from './shared';

// =============================================================================
// TYPES
// =============================================================================

type ViewType = 
  | 'cards'         // Grid of action cards
  | 'comparison'    // Side-by-side comparison
  | 'timeline'      // Chronological events
  | 'steps'         // Step-by-step wizard
  | 'chart'         // Data visualization
  | 'table'         // Structured data
  | 'carousel';     // Horizontal scroll

interface DynamicViewData {
  type: ViewType;
  title?: string;
  description?: string;
  items: DynamicItem[];
  actions?: DynamicAction[];
}

interface DynamicItem {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  image?: string;
  value?: string | number;
  metadata?: Record<string, string | number>;
  highlighted?: boolean;
  badge?: string;
}

interface DynamicAction {
  id: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
  prompt?: string;  // What to send to chat
}

// =============================================================================
// ICON MAPPING
// =============================================================================

const getIcon = (iconName?: string) => {
  const icons: Record<string, React.ComponentType<{ className?: string }>> = {
    calendar: Calendar,
    clock: Clock,
    mail: Mail,
    zap: Zap,
    trending: TrendingUp,
    check: CheckSquare,
    globe: Globe,
    arrow: ArrowUp,
    alert: AlertCircle,
  };
  return icons[iconName || 'zap'] || Zap;
};

// =============================================================================
// CARD VIEW - Grid of action cards
// =============================================================================

interface CardViewProps {
  items: DynamicItem[];
  onSelect?: (item: DynamicItem) => void;
  columns?: 2 | 3 | 4;
}

const CardView: React.FC<CardViewProps> = ({ 
  items, 
  onSelect,
  columns = 3 
}) => {
  const gridCols = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-4`}>
      {items.map((item) => {
        const Icon = getIcon(item.icon);
        return (
          <button
            key={item.id}
            onClick={() => onSelect?.(item)}
            className={`
              group relative p-4 rounded-2xl text-left transition-all duration-200
              bg-black/40 backdrop-blur-xl border
              ${item.highlighted 
                ? 'border-cyan-400/50 shadow-[0_0_20px_rgba(34,211,238,0.3)]' 
                : 'border-cyan-400/20 hover:border-cyan-400/40'}
              hover:bg-cyan-400/10
            `}
          >
            {/* Badge */}
            {item.badge && (
              <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-cyan-400/20 text-cyan-300 border border-cyan-400/30">
                {item.badge}
              </span>
            )}

            {/* Icon or Image */}
            {item.image ? (
              <img 
                src={item.image} 
                alt={item.title}
                className="w-full h-32 object-cover rounded-xl mb-3"
              />
            ) : (
              <div className="p-2 rounded-xl bg-cyan-400/10 w-fit mb-3 group-hover:bg-cyan-400/20 transition-colors">
                <Icon className="w-5 h-5 text-cyan-400" />
              </div>
            )}

            {/* Content */}
            <h4 className="text-cyan-300 font-semibold mb-1">{item.title}</h4>
            {item.subtitle && (
              <p className="text-cyan-400/50 text-sm mb-2">{item.subtitle}</p>
            )}
            {item.value && (
              <p className="text-cyan-400 font-bold text-lg">{item.value}</p>
            )}
            {item.description && (
              <p className="text-cyan-400/60 text-sm mt-2 line-clamp-2">{item.description}</p>
            )}

            {/* Hover arrow */}
            <ChevronRight className="absolute bottom-4 right-4 w-4 h-4 text-cyan-400/30 group-hover:text-cyan-300 group-hover:translate-x-1 transition-all" />
          </button>
        );
      })}
    </div>
  );
};

// =============================================================================
// COMPARISON VIEW - Side by side
// =============================================================================

interface ComparisonViewProps {
  items: DynamicItem[];
  onSelect?: (item: DynamicItem) => void;
}

const ComparisonView: React.FC<ComparisonViewProps> = ({ items, onSelect }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {items.map((item) => {
        const Icon = getIcon(item.icon);
        const isSelected = selectedId === item.id;

        return (
          <div
            key={item.id}
            onClick={() => {
              setSelectedId(item.id);
              onSelect?.(item);
            }}
            className={`
              flex-shrink-0 w-64 p-4 rounded-2xl cursor-pointer transition-all duration-200
              bg-black/40 backdrop-blur-xl border
              ${isSelected 
                ? 'border-cyan-400 shadow-[0_0_30px_rgba(34,211,238,0.4)] scale-105' 
                : 'border-cyan-400/20 hover:border-cyan-400/40'}
            `}
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-xl ${isSelected ? 'bg-cyan-400/30' : 'bg-cyan-400/10'}`}>
                <Icon className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h4 className="text-cyan-300 font-semibold">{item.title}</h4>
                {item.subtitle && (
                  <p className="text-cyan-400/50 text-xs">{item.subtitle}</p>
                )}
              </div>
            </div>

            {/* Price/Value */}
            {item.value && (
              <div className="text-2xl font-bold text-cyan-400 mb-3">{item.value}</div>
            )}

            {/* Metadata */}
            {item.metadata && (
              <div className="space-y-2">
                {Object.entries(item.metadata).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-cyan-400/50">{key}</span>
                    <span className="text-cyan-300">{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Selected indicator */}
            {isSelected && (
              <div className="mt-4 pt-4 border-t border-cyan-400/20">
                <span className="text-xs text-cyan-400 flex items-center gap-1">
                  <CheckSquare className="w-3 h-3" />
                  Selected
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// TIMELINE VIEW - Chronological events
// =============================================================================

interface TimelineViewProps {
  items: DynamicItem[];
  onSelect?: (item: DynamicItem) => void;
}

const TimelineView: React.FC<TimelineViewProps> = ({ items, onSelect }) => {
  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-4 top-0 bottom-0 w-px bg-cyan-400/20" />

      <div className="space-y-4">
        {items.map((item) => {
          const ItemIcon = getIcon(item.icon);
          return (
            <div
              key={item.id}
              onClick={() => onSelect?.(item)}
              className="relative pl-12 cursor-pointer group"
            >
              {/* Dot */}
              <div className={`
                absolute left-0 w-8 h-8 rounded-full flex items-center justify-center
                ${item.highlighted 
                  ? 'bg-cyan-400/30 border-2 border-cyan-400' 
                  : 'bg-black border border-cyan-400/30 group-hover:border-cyan-400/60'}
                transition-colors
              `}>
                <ItemIcon className="w-4 h-4 text-cyan-400" />
              </div>

              {/* Content */}
              <div className="p-3 rounded-xl bg-black/30 border border-cyan-400/10 group-hover:border-cyan-400/30 transition-colors">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-cyan-300 font-semibold">{item.title}</h4>
                  {item.subtitle && (
                    <span className="text-cyan-400/50 text-xs">{item.subtitle}</span>
                  )}
                </div>
                {item.description && (
                  <p className="text-cyan-400/60 text-sm">{item.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// STEPS VIEW - Step by step wizard
// =============================================================================

interface StepsViewProps {
  items: DynamicItem[];
  currentStep?: number;
  onStepClick?: (step: number) => void;
}

const StepsView: React.FC<StepsViewProps> = ({ 
  items, 
  currentStep = 0,
  onStepClick 
}) => {
  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;

        return (
          <div
            key={item.id}
            onClick={() => onStepClick?.(index)}
            className={`
              flex items-start gap-4 p-4 rounded-2xl cursor-pointer transition-all
              ${isActive 
                ? 'bg-cyan-400/10 border border-cyan-400/40' 
                : 'bg-black/20 border border-transparent hover:bg-black/40'}
            `}
          >
            {/* Step number */}
            <div className={`
              flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold
              ${isCompleted 
                ? 'bg-cyan-400 text-black' 
                : isActive 
                  ? 'bg-cyan-400/20 text-cyan-400 border-2 border-cyan-400' 
                  : 'bg-cyan-400/10 text-cyan-400/50 border border-cyan-400/20'}
            `}>
              {isCompleted ? <CheckSquare className="w-5 h-5" /> : index + 1}
            </div>

            {/* Content */}
            <div className="flex-1">
              <h4 className={`font-semibold ${isActive ? 'text-cyan-300' : 'text-cyan-400/60'}`}>
                {item.title}
              </h4>
              {item.description && (
                <p className={`text-sm mt-1 ${isActive ? 'text-cyan-400/70' : 'text-cyan-400/40'}`}>
                  {item.description}
                </p>
              )}
            </div>

            {/* Status */}
            {isActive && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-cyan-400/20 text-cyan-400">
                Current
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// CAROUSEL VIEW - Horizontal scroll
// =============================================================================

interface CarouselViewProps {
  items: DynamicItem[];
  onSelect?: (item: DynamicItem) => void;
}

const CarouselView: React.FC<CarouselViewProps> = ({ items, onSelect }) => {
  return (
    <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory">
      {items.map((item) => {
        const Icon = getIcon(item.icon);
        return (
          <div
            key={item.id}
            onClick={() => onSelect?.(item)}
            className="
              flex-shrink-0 w-72 snap-center cursor-pointer
              p-4 rounded-2xl bg-black/40 backdrop-blur-xl 
              border border-cyan-400/20 hover:border-cyan-400/40
              transition-all hover:scale-[1.02]
            "
          >
            {item.image ? (
              <img 
                src={item.image} 
                alt={item.title}
                className="w-full h-40 object-cover rounded-xl mb-3"
              />
            ) : (
              <div className="w-full h-40 rounded-xl bg-gradient-to-br from-cyan-400/10 to-purple-400/10 flex items-center justify-center mb-3">
                <Icon className="w-12 h-12 text-cyan-400/30" />
              </div>
            )}
            <h4 className="text-cyan-300 font-semibold">{item.title}</h4>
            {item.subtitle && (
              <p className="text-cyan-400/50 text-sm">{item.subtitle}</p>
            )}
            {item.value && (
              <p className="text-cyan-400 font-bold mt-2">{item.value}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// MAIN DYNAMIC VIEW COMPONENT
// =============================================================================

interface DynamicViewProps {
  data: DynamicViewData;
  onSelect?: (item: DynamicItem) => void;
  onAction?: (action: DynamicAction) => void;
}

export const DynamicView: React.FC<DynamicViewProps> = ({
  data,
  onSelect,
  onAction
}) => {
  const renderView = () => {
    switch (data.type) {
      case 'cards':
        return <CardView items={data.items} onSelect={onSelect} />;
      case 'comparison':
        return <ComparisonView items={data.items} onSelect={onSelect} />;
      case 'timeline':
        return <TimelineView items={data.items} onSelect={onSelect} />;
      case 'steps':
        return <StepsView items={data.items} />;
      case 'carousel':
        return <CarouselView items={data.items} onSelect={onSelect} />;
      default:
        return <CardView items={data.items} onSelect={onSelect} />;
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      {(data.title || data.description) && (
        <div className="mb-4">
          {data.title && (
            <h3 className="text-lg font-semibold text-cyan-300">{data.title}</h3>
          )}
          {data.description && (
            <p className="text-cyan-400/60 text-sm mt-1">{data.description}</p>
          )}
        </div>
      )}

      {/* View content */}
      {renderView()}

      {/* Actions */}
      {data.actions && data.actions.length > 0 && (
        <div className="flex gap-3 mt-6">
          {data.actions.map((action) => (
            <button
              key={action.id}
              onClick={() => onAction?.(action)}
              className={`
                px-4 py-2 rounded-xl font-medium text-sm transition-all
                ${action.variant === 'primary'
                  ? 'bg-cyan-400 text-black hover:bg-cyan-300'
                  : action.variant === 'secondary'
                    ? 'bg-cyan-400/20 text-cyan-400 hover:bg-cyan-400/30 border border-cyan-400/40'
                    : 'text-cyan-400/60 hover:text-cyan-300'}
              `}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// PARSER - Convert AI response to DynamicView data
// =============================================================================

interface ParsedView {
  type: ViewType;
  items: DynamicItem[];
}

export const parseDynamicView = (content: string): ParsedView | null => {
  // Look for structured data markers from AI
  const viewMatch = content.match(/\[DYNAMIC_VIEW:(\w+)\]/);
  if (!viewMatch) return null;

  const viewType = viewMatch[1] as ViewType;
  
  // Extract items from content
  // Format: [ITEM:id|title|subtitle|value|icon]
  const itemMatches = content.matchAll(/\[ITEM:([^|]+)\|([^|]+)\|([^|]*)\|([^|]*)\|([^\]]*)\]/g);
  const items: DynamicItem[] = [];
  
  for (const match of itemMatches) {
    items.push({
      id: match[1],
      title: match[2],
      subtitle: match[3] || undefined,
      value: match[4] || undefined,
      icon: match[5] || undefined
    });
  }

  if (items.length === 0) return null;

  return { type: viewType, items };
};

export default DynamicView;
